/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AssetsService, FormHelpersService, UserEventsService } from 'app/shared/services';
import { Constants } from 'app/shared/models/constants.model';
import { environment } from 'environments/environment';
import { TranscoderService } from 'app/shared/services/transcoder.service';
import { UtilsService } from 'app/shared/services/utils.service';
import * as fromSharedStore from 'app/shared/store';
import { Store } from '@ngrx/store';
import { filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import dayjs from 'dayjs';

@Component({
  selector: 'app-game-upload-modal',
  templateUrl: './game-upload-modal.component.html',
  styleUrls: ['./game-upload-modal.component.scss'],
})
export class GameUploadModalComponent implements OnInit {
  @Input() userEvents: any = [];

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('videoUpload') videoUpload: ElementRef;

  selectedTeams: any[];
  newGameForm: FormGroup;
  uploading = false;
  fileLoaded = false;
  fileDrag = false;
  file: File;
  invalidFile: boolean;
  meridians = [
    { value: 'AM', name: 'AM' },
    { value: 'PM', name: 'PM' },
  ];
  selectedMeridian = this.meridians[0].value;

  eventInput$ = new Subject<string>();
  allEventsList = [];
  eventsList = [];
  eventListOpened = false;
  searchTerm: string;

  readonly MODAL_TYPES = Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES;

  uploadProgress = 0;

  private ongoingUploadReq: any;
  private componentDestroyed: Subject<any> = new Subject();
  private currentUser: any;
  private newEvent: any;

  constructor(
    public formHelpersService: FormHelpersService,
    private assetsService: AssetsService,
    private transcoderService: TranscoderService,
    private userEventsService: UserEventsService,
    private sharedStore: Store<fromSharedStore.SharedState>,
    private utilsService: UtilsService
  ) {}

  ngOnInit() {
    this.initForm();

    this.eventInput$
      .pipe(
        tap((inputTxt) => {
          if (inputTxt && inputTxt.length) {
            this.eventsList = this.allEventsList.filter((ev) => ev.name.toLowerCase().includes(inputTxt.toLowerCase()));
          } else {
            this.eventsList = [...this.allEventsList];
          }
          this.searchTerm = inputTxt;
        })
      )
      .subscribe((inputTxt) => {
        if (inputTxt && inputTxt.length >= 2 && this.eventsList && this.eventsList.length) {
          this.eventListOpened = true;
        } else {
          this.eventListOpened = false;
        }
      });

    this.sharedStore
      .select(fromSharedStore.getUserData)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((currentUser) => {
        this.currentUser = currentUser.userData;

        this.userEventsService.getUserEventsByUserId(this.currentUser.id).subscribe((userEvents) => {
          if (userEvents && userEvents.length) {
            this.userEvents = userEvents;
            const userEventIds = {};
            this.allEventsList = userEvents
              .reduce((acc, ue) => {
                const newUEvent = {
                  ...ue,
                  name: ue.event && ue.event.name ? ue.event.name : ue.name,
                };
                if (!userEventIds[newUEvent.name]) {
                  acc.push(newUEvent);
                  userEventIds[newUEvent.name] = newUEvent.name;
                }
                return acc;
              }, [])
              .sort((a, b) => (a.name > b.name ? 1 : -1));
            this.userEvents = [...this.allEventsList];
          }
        });
      });
  }

  initForm() {
    this.newGameForm = new FormGroup({
      title: new FormControl('', Validators.required),
      event: new FormControl('', Validators.required),
      videoUrl: new FormControl('', Validators.required),
      originalVideoUrl: new FormControl(''),
      newFilename: new FormControl(''),
      eventDate: new FormControl('', Validators.required),
      eventTime: new FormControl(),
      selectedMeridian: new FormControl(this.selectedMeridian),
    });
  }

  close() {
    this.closeModal.emit(null);
  }

  save() {
    const gameDate = dayjs.utc(
      `${this.newGameForm.value.eventDate} ${this.newGameForm.value.eventTime} ${this.newGameForm.value.selectedMeridian}`,
      'MM/DD/YY HH:mm A'
    );

    if (this.newEvent.custom) {
      this.newGameForm.get('event').patchValue(this.newEvent.name);
    }

    if (this.newGameForm.invalid) {
      this.formHelpersService.markFieldsAsDirty(this.newGameForm);
      return;
    }

    const uploadedHr = {
      ...this.newGameForm.value,
      file: this.file,
    };
    this.uploading = true;
    this.uploadProgress = 0;

    this.ongoingUploadReq = this.assetsService
      .uploadFileToBucket(
        `${this.currentUser.username}/${uploadedHr.newFilename}`,
        uploadedHr.file,
        environment.userUploadBucketName
      )
      .pipe(
        tap((response) => {
          if (response.loaded && response.total) {
            this.uploadProgress = Math.ceil((100 * response.loaded) / response.total);
          }
        }),
        filter((response) => response.ok && response.type === HttpEventType.Response)
      )
      .subscribe((response) => {
        let savedAsset;
        let savedUserEvent;
        const pathSegments = uploadedHr.videoUrl.split('/');
        const createAsset$ = this.assetsService.addVideoAsset({
          contentType: 'video/mp4',
          published: false,
          metadata: { isUserUpload: true, gameDate },
          type: Constants.ASSET_TYPE.ATHLETE_GAME_VIDEO,
          name: uploadedHr.title,
          originalName: pathSegments[pathSegments.length - 1],
          url: uploadedHr.videoUrl,
        });
        const createUserEvent$ = this.createUserEvent();

        forkJoin([createAsset$, createUserEvent$])
          .pipe(
            mergeMap(([asset, userEvent]) => {
              savedUserEvent =
                userEvent && userEvent.name ? userEvent : this.allEventsList.find((ue) => ue.id === userEvent.id);
              savedAsset = asset;
              uploadedHr.asset = savedAsset;
              const setCustomAsset$ = this.assetsService.setCustomAsset(asset.id, this.currentUser.id);
              const setUserEventAsset$ = this.userEventsService.addUserEventAsset(userEvent.id, asset.id);
              return forkJoin([setCustomAsset$, setUserEventAsset$]);
            })
          )
          .subscribe(([asset, userEventAsset]) => {
            if (asset) {
              const env = environment.development ? 'development' : environment.staging ? 'staging' : 'production';
              this.transcoderService
                .pushGameToQueue({
                  path: uploadedHr.originalVideoUrl,
                  status: 0,
                  environment: env,
                  assetId: savedAsset.id,
                })
                .subscribe(() => {});
              this.closeModal.emit({ asset: uploadedHr.asset, userEvent: savedUserEvent });
            }
          });
      });
  }

  onEventSelect(selectedOption) {
    this.eventListOpened = false;
    this.newEvent = {
      ...selectedOption,
      custom: false,
    };
    this.newGameForm.get('event').patchValue(this.newEvent.id);
  }

  onNewEvent({ term }) {
    this.newEvent = {
      id: null,
      custom: true,
      name: term,
    };
    this.newGameForm.get('event').patchValue(this.newEvent.id);
  }

  get videoUrl() {
    return this.newGameForm.get('videoUrl').value;
  }

  ngOnDestroy() {
    if (this.ongoingUploadReq) {
      this.ongoingUploadReq.unsubscribe();
    }
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  resetFile() {
    this.file = null;
    this.videoUpload.nativeElement.value = '';
    this.initForm();
  }

  onDrop($event) {
    $event.preventDefault();
    this.fileDrag = false;
    this.fileChanged({ target: $event.dataTransfer });
  }

  onDragOver(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = true;
  }

  onDragLeave(event) {
    event.stopPropagation();
    event.preventDefault();
    this.fileDrag = false;
  }

  onEventClickOutside($event) {
    this.eventListOpened = false;
    this.newGameForm.get('event').markAsTouched();
  }

  fileChanged(event) {
    this.file = null;
    if (!event.target.files) {
      return;
    }

    const file: File = event.target.files[0];
    let extension = file.name.slice(file.name.lastIndexOf('.') + 1);
    if (!extension) {
      return;
    }
    extension = extension.toLowerCase();
    const hasValidFileType = extension === 'mp4' || extension === 'mpeg' || extension === 'mov';
    const hasValidFileSize = file.size >= 0.1 * Math.pow(1024, 2) && file.size <= 0.5 * Math.pow(1024, 3);
    this.invalidFile = !hasValidFileSize || !hasValidFileType;

    if (!this.invalidFile) {
      this.file = file;
      const randomSuffix = this.utilsService.generateRandomString(4);
      const newFilename = `${this.file.name.replace(`.${extension}`, '')}_${randomSuffix}.${extension}`;
      this.newGameForm.get('newFilename').setValue(newFilename);
      this.newGameForm
        .get('originalVideoUrl')
        .setValue(
          Constants.GS_ASSET_HOSTNAME +
            '/video_assets/' +
            `_user_uploaded_games/${this.currentUser.username}/${newFilename}`
        );
      this.newGameForm
        .get('videoUrl')
        .setValue(
          Constants.GS_ASSET_HOSTNAME +
            '/video_assets/' +
            `_user_uploaded_games/${this.currentUser.username}/${newFilename.replace(`.${extension}`, '.mp4')}`
        );
    }
  }

  private createUserEvent(): Observable<any> {
    /* if event value is a plain string, that's a custom value & we'll create a new user event) */
    if (this.newEvent.custom) {
      /* double check if value is not included in searchResults */
      const userEvent = this.userEvents.find(
        (ue) => ue.name && ue.name.toLowerCase() === this.newEvent.name.toLowerCase()
      );
      if (!userEvent) {
        const newUserEvent = {
          name: this.newEvent.name,
          date: dayjs(this.newGameForm.value.eventDate, 'MM/DD/YY').format(),
          teamName: '',
          jersey: '',
          city: '',
          state: '',
          user: {
            id: this.currentUser.id,
          },
        };
        return this.userEventsService.addUserEvent(newUserEvent);
      } else {
        return of(userEvent);
      }
    } else {
      return of({ id: this.newGameForm.value.event });
    }
  }
}
